import { Fragment, useRef, type ReactNode } from "react";
import { Entity } from "@common/types/entity";
import { Picker } from "@design-system/Picker/Picker.lib";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import { PickerEntityItem } from "@components/entity/PickerEntityItem";
import { NO_ENTITY_LABEL, NO_ENTITY_VALUE } from "./types";

export interface EntityFilterGroup {
    title: string;
    entities: Entity[];
    /** Values the section title checkbox toggles; omitted, the section has no select-all. */
    selectAllValues?: number[];
}

/**
 * Splits the entities into the Business and Personal sections the entity
 * pickers show, keeping the rows whose name matches `search`. `onlyBusiness`
 * drops non-business entities and the Personal section.
 */
export const groupEntitiesForFilter = ({
    entities,
    onlyBusiness,
    search,
}: {
    entities: Entity[];
    onlyBusiness?: boolean;
    search: string;
}) => {
    const query = search.toLowerCase();
    const matchingEntities = entities.filter(
        (entity) =>
            (!onlyBusiness || entity.isBusiness) &&
            (entity.name ?? "").toLowerCase().includes(query),
    );

    return {
        businessEntities: matchingEntities.filter(
            (entity) => !entity.isPersonal,
        ),
        personalEntities: onlyBusiness
            ? []
            : matchingEntities.filter((entity) => entity.isPersonal),
    };
};

/** Whether the "No entity" row stays visible for the typed `search`. */
export const matchesNoEntityOption = (search: string) =>
    NO_ENTITY_LABEL.toLowerCase().includes(search.trim().toLowerCase());

interface EntityFilterPopupProps {
    groups: EntityFilterGroup[];
    container?: HTMLElement;
    isEntityDisabled: (entity: Entity) => boolean;
    disabledEntityTooltip?: ReactNode;
    /** Shows the Deselect footer while above 0. */
    selectedCount: number;
    onDeselect: () => void;
    /** Appends the "No entity" row below the sections, behind a separator. */
    showNoEntityOption?: boolean;
    search?: string;
    "aria-label"?: string;
}

/**
 * Popup body of the entity pickers: the search, the non-empty Business /
 * Personal sections of entity rows, the optional "No entity" row and the
 * Deselect footer. Deselecting unmounts the footer, so focus goes back to the
 * search instead of `<body>`.
 */
export const EntityFilterPopup = ({
    groups,
    container,
    isEntityDisabled,
    disabledEntityTooltip,
    selectedCount,
    onDeselect,
    showNoEntityOption = false,
    search = "",
    "aria-label": ariaLabel,
}: EntityFilterPopupProps) => {
    const searchRef = useRef<HTMLInputElement>(null);
    const visibleGroups = groups.filter((group) => group.entities.length > 0);
    const isNoEntityRowVisible =
        showNoEntityOption && matchesNoEntityOption(search);
    const handleDeselect = () => {
        onDeselect();
        searchRef.current?.focus({ preventScroll: true });
    };

    return (
        <Picker.Popup container={container} aria-label={ariaLabel}>
            <Picker.Search placeholder="Search..." ref={searchRef} />
            <Picker.List>
                {visibleGroups.map((group, index) => (
                    <Fragment key={group.title}>
                        {index > 0 && <Picker.Separator />}
                        <Picker.Section>
                            <Picker.SectionTitle
                                selectAll={
                                    group.selectAllValues === undefined
                                        ? undefined
                                        : { values: group.selectAllValues }
                                }
                            >
                                {group.title}
                            </Picker.SectionTitle>
                            {group.entities.map((entity) => (
                                <PickerEntityItem
                                    key={entity.id}
                                    value={entity.id}
                                    entity={entity}
                                    disabled={isEntityDisabled(entity)}
                                    disabledReason={disabledEntityTooltip}
                                />
                            ))}
                        </Picker.Section>
                    </Fragment>
                ))}
                {isNoEntityRowVisible && (
                    <>
                        {visibleGroups.length > 0 && <Picker.Separator />}
                        <Picker.Item value={NO_ENTITY_VALUE}>
                            {NO_ENTITY_LABEL}
                        </Picker.Item>
                    </>
                )}
                {visibleGroups.length === 0 && !isNoEntityRowVisible && (
                    <Picker.Empty>No matching entities</Picker.Empty>
                )}
            </Picker.List>
            {selectedCount > 0 && (
                <Picker.Footer>
                    <FilterActionsFooter
                        deselectFunction={handleDeselect}
                        selectedCount={selectedCount}
                        hideBorderAndPadding
                    />
                </Picker.Footer>
            )}
        </Picker.Popup>
    );
};
